import { AuthError, FunctionsHttpError, FunctionsRelayError, FunctionsFetchError } from '@supabase/supabase-js';
import { authLogger } from './logging';

export interface AuthErrorDetails {
  type: 'user_not_found' | 'session_expired' | 'invalid_token' | 'insufficient_permissions' | 'network_error' | 'unknown';
  originalError: unknown;
  shouldRedirectToLogin: boolean;
  shouldClearSession: boolean;
  userMessage?: string;
  code?: string; // Optional error code for specific error types (e.g., 'NETWORK_ERROR')
}

/**
 * Centralized auth error detection and handling utility
 * Analyzes errors from Supabase operations and determines appropriate actions
 */
export class AuthErrorInterceptor {
  private static redirectInProgress = false;
  
  /**
   * Analyze an error to determine if it's auth-related and what action to take
   */
  static analyzeError(error: unknown): AuthErrorDetails | null {
    if (!error) return null;

    // Handle Supabase AuthError objects
    if ((error as { __isAuthError?: boolean }).__isAuthError || error instanceof AuthError) {
      return this.handleAuthError(error as AuthError);
    }

    // Handle Edge Function errors with auth context
    if ((error as { message?: string; code?: string; status?: number }).message ||
        (error as { message?: string; code?: string; status?: number }).code ||
        (error as { message?: string; code?: string; status?: number }).status) {
      return this.handleGenericError(error as { message?: string; code?: string; status?: number });
    }

    return null;
  }

  /**
   * Enhanced error analysis for Supabase-specific error types
   * Handles FunctionsFetchError, FunctionsHttpError, and FunctionsRelayError
   */
  static async analyzeSupabaseError(error: unknown): Promise<AuthErrorDetails | null> {
    if (!error) return null;

    authLogger.warn('[AuthErrorInterceptor] Analyzing Supabase error:', {
      errorType: (error as { constructor?: { name?: string } }).constructor?.name,
      isInstance: {
        FunctionsFetchError: error instanceof FunctionsFetchError,
        FunctionsHttpError: error instanceof FunctionsHttpError,
        FunctionsRelayError: error instanceof FunctionsRelayError,
        AuthError: error instanceof AuthError || (error as { __isAuthError?: boolean }).__isAuthError
      },
      message: (error as { message?: string }).message,
      status: (error as { status?: number }).status
    });

    // Handle FunctionsFetchError (Edge Runtime JWT rejection)
    if (error instanceof FunctionsFetchError) {
      authLogger.warn('[AuthErrorInterceptor] FunctionsFetchError detected - likely Edge Runtime JWT rejection');

      // This occurs when Edge Runtime rejects JWT before reaching our function
      if (error.message && (
        error.message.includes('Failed to send a request') ||
        error.message.includes('edge function') ||
        error.message.includes('fetch')
      )) {
        return {
          type: 'invalid_token',
          originalError: error,
          shouldRedirectToLogin: true,
          shouldClearSession: true,
          userMessage: 'Your session has expired. Please log in again.'
        };
      }
    }

    // Handle FunctionsHttpError (function ran but returned error)
    if (error instanceof FunctionsHttpError) {
      authLogger.warn('[AuthErrorInterceptor] FunctionsHttpError detected - analyzing response context');

      try {
        // Check if response body was already consumed
        if ((error.context as { bodyUsed?: boolean }).bodyUsed) {
          authLogger.warn('[AuthErrorInterceptor] Response body already consumed, falling back to status code analysis');
          // Fall back to status code immediately
          if ((error as { status?: number }).status === 401) {
            return {
              type: 'insufficient_permissions',
              originalError: error,
              shouldRedirectToLogin: true,
              shouldClearSession: true,
              userMessage: 'Authentication required. Please log in.'
            };
          }
          // For other status codes, return null to use default handling
          return null;
        }

        const context = await error.context.json();
        authLogger.warn('[AuthErrorInterceptor] FunctionsHttpError context:', context);

        // Analyze the actual error response from the Edge Function
        return this.analyzeError(context);
      } catch (contextError) {
        authLogger.warn('[AuthErrorInterceptor] Failed to parse FunctionsHttpError context:', contextError);

        // Fall back to status code analysis
        if ((error as { status?: number }).status === 401) {
          return {
            type: 'insufficient_permissions',
            originalError: error,
            shouldRedirectToLogin: true,
            shouldClearSession: true,
            userMessage: 'Authentication required. Please log in.'
          };
        }
      }
    }

    // Handle FunctionsRelayError (network issues between client and Supabase)
    if (error instanceof FunctionsRelayError) {
      authLogger.warn('[AuthErrorInterceptor] FunctionsRelayError detected - network issue');

      return {
        type: 'network_error',
        originalError: error,
        shouldRedirectToLogin: false,
        shouldClearSession: false,
        userMessage: 'Connection error. Please check your network and try again.'
      };
    }

    // Fall back to existing generic analysis
    return this.analyzeError(error);
  }

  /**
   * Handle Supabase AuthError objects
   */
  private static handleAuthError(error: AuthError): AuthErrorDetails {
    const code = error.code || '';
    const message = error.message || '';
    const status = (error as { status?: number }).status || 0;

    // User not found - account deleted or deactivated
    if (code === 'user_not_found' || status === 403) {
      return {
        type: 'user_not_found',
        originalError: error,
        shouldRedirectToLogin: true,
        shouldClearSession: true,
        userMessage: 'Your session has expired. Please log in again.'
      };
    }

    // Session or token issues including JWS/JWT errors
    if (code.includes('session') || code.includes('token') || message.includes('session') || message.includes('token') ||
        code === 'err_jws_invalid' || message.includes('jws protected header is invalid') ||
        message.includes('jwsinvalid') || error.name === 'JWSInvalid') {
      return {
        type: 'invalid_token',
        originalError: error,
        shouldRedirectToLogin: true,
        shouldClearSession: true,
        userMessage: 'Your session has expired. Please log in again.'
      };
    }

    // Invalid or malformed token
    if (code.includes('invalid') || message.includes('invalid')) {
      return {
        type: 'invalid_token',
        originalError: error,
        shouldRedirectToLogin: true,
        shouldClearSession: true,
        userMessage: 'Authentication error. Please log in again.'
      };
    }

    // Permission-related errors
    if (message.includes('permission') || message.includes('unauthorized') || status === 401) {
      return {
        type: 'insufficient_permissions',
        originalError: error,
        shouldRedirectToLogin: true,
        shouldClearSession: true,
        userMessage: 'Access denied. Please log in with appropriate permissions.'
      };
    }

    // Unknown auth error
    return {
      type: 'unknown',
      originalError: error,
      shouldRedirectToLogin: true,
      shouldClearSession: true,
      userMessage: 'Authentication error. Please log in again.'
    };
  }

  /**
   * Check if an error is likely a cold start or deployment race condition
   * These should be handled with silent retries, not user-facing error messages
   */
  static isColdStartError(error: unknown): boolean {
    const typedError = error as { code?: string; status?: number; message?: string };
    const code = (typedError.code || '').toLowerCase();
    const status = typedError.status || 0;
    const message = (typedError.message || '').toLowerCase();

    return (
      status === 503 ||
      status === 409 ||
      code === 'cold_start_retries_exhausted' ||
      code === 'deployment_race_condition_exhausted' ||
      code === 'auth_deployment_race_condition' ||
      code === 'auth_race_condition' ||
      (status === 500 && message.includes('function invocation failed'))
    );
  }

  /**
   * Handle generic errors that might be auth-related
   */
  private static handleGenericError(error: unknown): AuthErrorDetails | null {
    const typedError = error as { code?: string; status?: number; message?: string };
    const message = (typedError.message || '').toLowerCase();
    const code = (typedError.code || '').toLowerCase();
    const status = typedError.status || 0;

    // Session validation timeout — NOT a real auth error.
    // This happens when getSession() times out due to navigator.locks contention
    // (e.g. _recoverAndRefresh holding the lock during initialization).
    // Must NOT classify as invalid_token — that would trigger clearAuthState/signOut.
    if (code === 'session_validation_timeout' || message.includes('session validation timed out')) {
      return {
        type: 'network_error',
        originalError: error,
        shouldRedirectToLogin: false,
        shouldClearSession: false,
        userMessage: 'Session check is taking longer than expected. Please try again.'
      };
    }

    // Check for cold start errors first - these should not show user error messages during retries
    if (this.isColdStartError(error)) {
      // Only show error to user if retries are exhausted
      if (code === 'cold_start_retries_exhausted' || code === 'deployment_race_condition_exhausted') {
        return {
          type: 'network_error',
          originalError: error,
          shouldRedirectToLogin: false,
          shouldClearSession: false,
          userMessage: 'The system is starting up. Please refresh the page and try again.'
        };
      }

      // For ongoing cold start retries, don't show any error to user
      return null;
    }

    // User not found errors - check this first and be specific
    if (code === 'user_not_found' || status === 403 || message.includes('user_not_found')) {
      return {
        type: 'user_not_found',
        originalError: error,
        shouldRedirectToLogin: true,
        shouldClearSession: true,
        userMessage: 'Your session has expired. Please log in again.'
      };
    }

    // Edge Function JWT verification errors - our new error codes
    if (code === 'invalid_jwt_token' || code === 'jwt_verification_failed' || 
        message.includes('token verification failed') || message.includes('token verification error')) {
      return {
        type: 'invalid_token',
        originalError: error,
        shouldRedirectToLogin: true,
        shouldClearSession: true,
        userMessage: 'Your session has expired. Please log in again.'
      };
    }

    // Gateway-level JWT rejection patterns (discovered during testing)
    if (
      // CORS errors from JWT rejection at gateway level
      message.includes('cors policy') || 
      message.includes('access-control-allow-origin') ||
      // Direct "Failed to send request" errors
      message.includes('failed to send a request to the edge function') ||
      message.includes('failed to send a request to a edge function') ||
      // JWS/JWT decode errors from Supabase
      message.includes('jwserror') || 
      message.includes('jsondecode error') ||
      message.includes('protected header contains invalid json') ||
      code === 'pgrst301' ||
      // Generic fetch failures that might be JWT-related
      (message.includes('failed to fetch') && status === 401) ||
      (message.includes('net::err_failed') && status === 401)
    ) {
      authLogger.warn('[AuthErrorInterceptor] Gateway-level JWT rejection detected');
      return {
        type: 'invalid_token',
        originalError: error,
        shouldRedirectToLogin: true,
        shouldClearSession: true,
        userMessage: 'Your session has expired. Please log in again.'
      };
    }

    // Session/token related errors - be more specific
    if (code.includes('session') || code.includes('token') || 
        message.includes('session expired') || message.includes('invalid jwt') || 
        message.includes('token expired') || message.includes('auth session missing') ||
        // Add JWS/JWT specific errors from Edge Functions
        code === 'err_jws_invalid' || message.includes('jws protected header is invalid') ||
        message.includes('jwsinvalid') || (error as { name?: string }).name === 'JWSInvalid' ||
        message.includes('jwt verification failed') || message.includes('invalid signature')) {
      return {
        type: 'invalid_token',
        originalError: error,
        shouldRedirectToLogin: true,
        shouldClearSession: true,
        userMessage: 'Your session has expired. Please log in again.'
      };
    }

    // Unauthorized errors
    if (status === 401 || message.includes('unauthorized') || message.includes('unauthenticated')) {
      return {
        type: 'insufficient_permissions',
        originalError: error,
        shouldRedirectToLogin: true,
        shouldClearSession: true,
        userMessage: 'Authentication required. Please log in.'
      };
    }

    // Check for network errors last and be very specific
    if (status === 0 || 
        (message.includes('network') && message.includes('failed')) || 
        (message.includes('fetch') && message.includes('failed')) ||
        message.includes('connection refused') ||
        message.includes('timeout') ||
        code === 'network_error') {
      return {
        type: 'network_error',
        originalError: error,
        shouldRedirectToLogin: false,
        shouldClearSession: false,
        userMessage: 'Network error. Please check your connection and try again.'
      };
    }

    return null;
  }

  /**
   * Execute the appropriate action based on error analysis
   * This should be called from the auth context or error handlers
   */
  static async handleAuthErrorAction(
    errorDetails: AuthErrorDetails, 
    clearAuthState: () => Promise<void>,
    redirectToLogin: () => void
  ): Promise<void> {
    // Prevent multiple simultaneous redirects
    if (this.redirectInProgress && errorDetails.shouldRedirectToLogin) {
      authLogger.warn('[AuthErrorInterceptor] Redirect already in progress, skipping');
      return;
    }

    authLogger.error('[AuthErrorInterceptor] Handling auth error:', {
      type: errorDetails.type,
      shouldRedirectToLogin: errorDetails.shouldRedirectToLogin,
      shouldClearSession: errorDetails.shouldClearSession,
      originalError: errorDetails.originalError
    });

    // Clear session if needed
    if (errorDetails.shouldClearSession) {
      try {
        await clearAuthState();
        authLogger.warn('[AuthErrorInterceptor] Auth state cleared');
      } catch (_error) {
        authLogger.error('Failed operation:', _error);
      }
    }

    // Redirect to login if needed
    if (errorDetails.shouldRedirectToLogin) {
      this.redirectInProgress = true;
      
      try {
        // Small delay to allow state clearing to complete
        setTimeout(() => {
          redirectToLogin();
          // Reset redirect flag after a reasonable delay
          setTimeout(() => {
            this.redirectInProgress = false;
          }, 2000);
        }, 100);
      } catch (_error) {
        authLogger.error('Failed operation:', _error);
        this.redirectInProgress = false;
      }
    }
  }

  /**
   * Check if an error should be ignored (e.g., user cancelled operations)
   */
  static shouldIgnoreError(error: unknown): boolean {
    if (!error) return true;

    const typedError = error as { message?: string };
    const message = (typedError.message || '').toLowerCase();
    
    // Ignore user-cancelled operations
    if (message.includes('cancelled') || message.includes('aborted')) {
      return true;
    }

    // Ignore network errors that are likely temporary
    if (message.includes('fetch') && message.includes('failed')) {
      return true;
    }

    return false;
  }

  /**
   * Reset the redirect protection flag (useful for testing)
   */
  static resetRedirectFlag(): void {
    this.redirectInProgress = false;
  }
}